// =============================================================================
// trackerRequests.js
//
// Chamadas HTTP à API do Tracker.
//
// Regras:
//   1. Cada função faz UMA chamada a UM endpoint.
//   2. Devolvem a Promise do Axios — quem chama decide o que fazer.
//   3. Nenhuma função toca no state ou no dispatch (isso é dos thunks).
// =============================================================================

import config from '../../../configuration/configuration.js';
import AxiosRequest from '../../../utils/AxiosRequest.js';

const BASE = `${config.API_URL}Tracker`;

// ── CRUD de entradas ──────────────────────────────────────────────────────────

export const apiGetAll = () =>
	AxiosRequest({ method: 'GET', url: `${BASE}/GetAll` });

export const apiGetById = (id) =>
	AxiosRequest({ method: 'GET', url: `${BASE}/GetById`, params: { id } });

// Body inclui auditUserId e auditUserName para o audit log
export const apiCreate = (data) =>
	AxiosRequest({ method: 'POST', url: `${BASE}/Create`, data });

export const apiUpdate = (id, data) =>
	AxiosRequest({ method: 'POST', url: `${BASE}/Update`, params: { id }, data });

// Soft delete — envia userId, userName e entryTitle como query params
export const apiDelete = (id, userId, entryTitle) =>
	AxiosRequest({ method: 'POST', url: `${BASE}/Delete`, params: { id, userId, entryTitle } });

// Restore — reverte o soft delete
export const apiRestore = (id, userId, entryTitle) =>
	AxiosRequest({ method: 'POST', url: `${BASE}/Restore`, params: { id, userId, entryTitle } });

// ── Export ────────────────────────────────────────────────────────────────────
// Usam Axios com responseType: 'blob' para respeitar o token de autenticação.
// O download é acionado programaticamente via URL temporário.

const downloadBlob = (data, filename) => {
	const url  = window.URL.createObjectURL(new Blob([data]));
	const link = document.createElement('a');
	link.href  = url;
	link.setAttribute('download', filename);
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
};

export const apiExportAll = () =>
	AxiosRequest({ method: 'GET', url: `${BASE}/ExportAll`, responseType: 'blob' })
		.then((res) => {
			const date     = new Date().toISOString().slice(0, 10);
			const filename = `NetworkActivityTracker_${date}.xlsx`;
			downloadBlob(res.data, filename);
		});

export const apiExportById = (id, title) =>
	AxiosRequest({ method: 'GET', url: `${BASE}/ExportById`, params: { id }, responseType: 'blob' })
		.then((res) => {
			const safe     = (title ?? `Entry_${id}`).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
			const date     = new Date().toISOString().slice(0, 10);
			const filename = `Tracker_${safe}_${date}.xlsx`;
			downloadBlob(res.data, filename);
		});

export const apiGetAuditLog = (entryId) =>
	AxiosRequest({ method: 'GET', url: `${BASE}/GetAuditLog`, params: { entryId } });

// ── Lookups — dados para dropdowns ───────────────────────────────────────────

export const apiGetServers = (technology, vendor) =>
	AxiosRequest({ method: 'GET', url: `${BASE}/GetServers`, params: { technology, vendor } });

export const apiGetTechnologies = () =>
	AxiosRequest({ method: 'GET', url: `${BASE}/GetTechnologies` });

export const apiGetVendors = () =>
	AxiosRequest({ method: 'GET', url: `${BASE}/GetVendors` });

export const apiGetFeatures = () =>
	AxiosRequest({ method: 'GET', url: `${BASE}/GetFeatures` });

export const apiGetTeams = () =>
	AxiosRequest({ method: 'GET', url: `${BASE}/GetTeams` });

export const apiGetOwnersByTeam = (teamId) =>
	AxiosRequest({ method: 'GET', url: `${BASE}/GetOwnersByTeam`, params: { teamId } });

// ── Add by Site ───────────────────────────────────────────────────────────────
// Body: { technology: string, sites: string[] }
// Returns: [{ csr, omcId, tac, found }]
export const apiGetServersBySites = (technology, sites) =>
	AxiosRequest({ method: 'POST', url: `${BASE}/GetServersBySites`, data: { technology, sites } });